import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { DEFAULT_DESCRIPTION, DEFAULT_IMAGE, SITE_NAME, SITE_TAGLINE, absoluteUrl } from "@/lib/seo";

export interface SEOOptions {
  /** Absolute URL of the social share image; defaults to the site card. */
  image?: string;
  /** Open Graph type: "article" for a single piece of content. */
  type?: "website" | "article";
  /** Keep the page out of search results (private pages, not-found states). */
  noindex?: boolean;
  /** Canonical path when the page is reachable under more than one URL; defaults to the current path. */
  path?: string;
}

const HOME_TITLE = `${SITE_NAME} — ${SITE_TAGLINE}`;

function setTag(selector: string, create: () => HTMLElement, attr: string, value: string | null) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (value === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setMeta(key: "name" | "property", id: string, content: string | null) {
  setTag(`meta[${key}="${id}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute(key, id);
    return m;
  }, "content", content);
}

/**
 * Sets the document title, description, canonical URL, robots directive and
 * Open Graph / Twitter card tags for the current route. The deploy step
 * prerenders public pages, so these end up in the static HTML crawlers read.
 */
export function useSEO(title?: string, description?: string, options: SEOOptions = {}) {
  const { pathname } = useLocation();
  const { image = DEFAULT_IMAGE, type = "website", noindex = false, path = pathname } = options;

  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE_NAME}` : HOME_TITLE;
    const desc = description || DEFAULT_DESCRIPTION;
    const url = absoluteUrl(path);

    document.title = fullTitle;
    setMeta("name", "description", desc);
    setMeta("name", "robots", noindex ? "noindex" : null);
    setTag('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    }, "href", noindex ? null : url);

    setMeta("property", "og:title", title ?? HOME_TITLE);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", image);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title ?? HOME_TITLE);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", image);
  }, [title, description, path, image, type, noindex]);
}
